import { Check, Lock, Shield } from 'lucide-react';
import { cn } from '@/utils/cn';
import { buildPermissionKey } from '@/modules/settings/permissions/catalog/permissionCatalog';
import type { AvailableModule, RoleEditData } from '../types/rolesEdit.types';

interface RoleEditFormBlockProps {
  formData: Partial<RoleEditData>;
  availableModules: AvailableModule[];
  isLoading?: boolean;
  onChange: (data: Partial<RoleEditData>) => void;
}

export function RoleEditFormBlock({ formData, availableModules, isLoading, onChange }: RoleEditFormBlockProps) {
  const permissionsJson = formData.permissionsJson || {};

  const hasPermission = (moduleCode: string, resourceCode: string, actionCode: string) => {
    const value = resourceCode === 'general' ? actionCode : `${resourceCode}.${actionCode}`;
    return (permissionsJson[moduleCode] || []).includes(value);
  };

  const togglePermission = (moduleCode: string, resourceCode: string, actionCode: string) => {
    const value = resourceCode === 'general' ? actionCode : `${resourceCode}.${actionCode}`;
    const current = permissionsJson[moduleCode] || [];
    const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
    const updated = { ...permissionsJson };
    if (next.length) updated[moduleCode] = next;
    else delete updated[moduleCode];
    onChange({ ...formData, permissionsJson: updated });
  };

  const toggleModule = (moduleCode: string) => {
    const module = availableModules.find((m) => m.code === moduleCode);
    if (!module) return;

    const allKeys = module.resources.flatMap((r) =>
      r.actions.map((a) => (r.code === 'general' ? a.code : `${r.code}.${a.code}`)),
    );

    const current = permissionsJson[moduleCode] || [];
    const isAllSelected = allKeys.every((key) => current.includes(key));

    const updated = { ...permissionsJson };
    if (isAllSelected) {
      delete updated[moduleCode];
    } else {
      updated[moduleCode] = allKeys;
    }

    onChange({ ...formData, permissionsJson: updated });
  };

  if (isLoading) return <div className="h-64 animate-pulse rounded-md border border-slate-100 bg-white p-3" />;

  return (
    <div className="space-y-4">
      <section className="rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
            <Shield className="h-4 w-4 text-emerald-600" />
            <h2 className="font-medium text-slate-900">Thông tin vai trò</h2>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-2 text-sm">
              <span className="font-semibold text-slate-600">Tên vai trò</span>
              <input
                value={formData.name || ''}
                onChange={(event) => onChange({ ...formData, name: event.target.value })}
                className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-emerald-500"
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-semibold text-slate-600">Mã định danh</span>
              <input
                value={formData.code || ''}
                readOnly
                className="h-10 w-full rounded-md border border-slate-100 bg-slate-50 px-3 text-sm text-slate-500 outline-none"
              />
            </label>
            <label className="space-y-2 text-sm md:col-span-2">
              <span className="font-semibold text-slate-600">Mô tả</span>
              <textarea
                value={formData.description || ''}
                onChange={(event) => onChange({ ...formData, description: event.target.value })}
                rows={3}
                className="w-full resize-none rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-emerald-500"
              />
            </label>
          </div>
        </div>
      </section>

      <section className="rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
            <Lock className="h-4 w-4 text-rose-600" />
            <h2 className="font-medium text-slate-900">Quyền và phạm vi</h2>
          </div>
          <div className="space-y-3">
            {availableModules.map((module) => {
              const moduleKeys = module.resources.flatMap((r) =>
                r.actions.map((a) => (r.code === 'general' ? a.code : `${r.code}.${a.code}`)),
              );
              const currentModuleKeys = permissionsJson[module.code] || [];
              const isAllSelected = moduleKeys.length > 0 && moduleKeys.every((k) => currentModuleKeys.includes(k));

              return (
                <div key={module.code} className="rounded-md border border-slate-100 p-3">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div className="space-y-1">
                        <h3 className="font-medium text-slate-900">{module.name}</h3>
                        <p className="text-slate-500">{module.description}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleModule(module.code)}
                        className={cn(
                          'h-8 rounded-md border px-3 text-xs font-semibold transition-colors',
                          isAllSelected ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100' : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                        )}
                      >
                        {isAllSelected ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                      </button>
                    </div>
                    <div className="space-y-3">
                      {module.resources.map((resource) => (
                    <div key={resource.code} className="rounded-md bg-slate-50 p-3">
                      <div className="space-y-2 text-sm">
                        <p className="font-semibold text-slate-800">{resource.name}</p>
                        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                          {resource.actions.map((action) => {
                            const checked = hasPermission(module.code, resource.code, action.code);
                            const key = buildPermissionKey(module.code, resource.code, action.code);
                            return (
                              <label
                                key={key}
                                className={cn(
                                  'flex cursor-pointer items-start gap-3 rounded-md border bg-white p-3 text-sm',
                                  checked ? 'border-emerald-200' : 'border-slate-100',
                                )}
                              >
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={checked}
                                  onChange={() => togglePermission(module.code, resource.code, action.code)}
                                />
                                <span className={cn('mt-1 flex h-5 w-5 items-center justify-center rounded-md border', checked ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-200')}>
                                  {checked && <Check className="h-4 w-4" />}
                                </span>
                                <span className="min-w-0 flex-1 space-y-2">
                                  <span className="flex items-center justify-between gap-2">
                                    <span className="font-semibold text-slate-900">{action.name}</span>
                                    <span className={cn('rounded-md px-2 py-1 text-sm', action.risk === 'Rất cao' || action.risk === 'Cao' ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600')}>
                                      {action.risk}
                                    </span>
                                  </span>
                                  <span className="block text-slate-500">{action.description}</span>
                                  <span className="block text-slate-400">Phạm vi: {action.scopes.join(', ')}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
